// store.ts — the browser's copy of the encounter mini-map graph, one per campaign (owner 2026-09-10:
// the map belongs to the front end; the back end saves and publishes).
//
// Owner ruling, same night: THE BROWSER FILTERS ALL OF IT. Every campaign member's store holds the
// campaign's RAW graph — private links, unexplored room names, secret passages — and buildMinimapView()
// decides what the viewer has earned (projectPlayerView for players; the full web only for a GM).
// Accepted trade-off: a player can read spoilers in devtools. Upside: everyone lays out the same full
// graph, so all viewers see the same web and a reveal never moves a room.
//
// No reads, ever (the old options/api/encounter_minimap_inc.php is gone):
//   - BOOT: the page's boot island carries its campaign's graph (window.FateTide.minimapGraph,
//     ft_encounter_minimap_boot_payload).
//   - PLAY: a room's first entry arrives as a `minimap_graph_changed` push carrying that room's raw
//     neighbourhood; it merges in place.
//   - AUTHORING: any other graph edit arrives as a push carrying the FULL rebuilt graph; it replaces ours.
// Frames carry a per-campaign revision; an older one than we hold is ignored. A WebSocket gap can drop a
// push — the map then shows the older state until the next push or page load (no history is replayed).
//
// Layout (layout.ts) is memoized per graph revision and REMEMBERED per campaign in localStorage, so a GM's
// link edit doesn't reshuffle the rooms already on screen.

import { layoutMinimap, projectPlayerView, type LayoutPoint, type LayoutSeed } from './layout';

type Passage = { name: string; type: string };

export interface StoreNode {
    id: string;
    name: string;
    explored: boolean;
    seed?: LayoutSeed;
}

export interface StoreEdge {
    from: string;
    to: string;
    private: boolean;
    /** Passage realized by a PUBLIC link row — the only one a player's view may show. */
    passage: Passage | null;
    /** A passage seen only on private rows (a secret door) — GM view only. */
    privatePassage: Passage | null;
}

export interface CampaignGraph {
    campaignId: string;
    /** The viewer may see the full GM web (campaign GM / staff). */
    viewerGm: boolean;
    /** Revision of the FULL graph this store was built from (boot island or full push). Every write happens
     *  before its revision is taken and a full graph is built after its own, so a reveal at or below this
     *  revision is already inside the graph. */
    rev: number;
    /** Reveals applied on top of the full graph, kept so a full push that was built earlier but ARRIVES
     *  later (its rebuild outran by a reveal's publish) can have them re-applied instead of losing them. */
    reveals: Array<{ rev: number; reveal: MinimapReveal }>;
    /** Newest reveal revision applied per room — an older reveal for the same room arriving late is ignored. */
    roomRevealRev: Map<string, number>;
    nodes: Map<string, StoreNode>;
    edges: Map<string, StoreEdge>;
    /** Local change counter — keys the layout memo. */
    revision: number;
}

interface WireEdge {
    from: string;
    to: string;
    private?: boolean;
    passage?: Passage | null;
    private_passage?: Passage | null;
}

export interface GraphPayload {
    campaignId?: string;
    rev?: number;
    viewerGm?: boolean;
    nodes?: Array<{ id: string; name?: string; explored?: number; seed?: { x: number; y: number; frame?: string } }>;
    edges?: WireEdge[];
}

export interface MinimapReveal {
    encounter_id: string;
    name: string;
    edges: WireEdge[];
    neighbors: Array<{ id: string; name: string; explored: number }>;
}

export interface ViewNode {
    id: string;
    name: string;
    explored: number;
    current: boolean;
    frontier: boolean;
    x: number;
    y: number;
}

export interface ViewEdge {
    from: string;
    to: string;
    private?: boolean;
    passage?: Passage | null;
}

const graphs = new Map<string, CampaignGraph>();
const listeners = new Set<(campaignId: string) => void>();
const layoutMemo = new Map<string, { revision: number; positions: Record<string, LayoutPoint> }>();
let bootSeeded = false;

const norm = (id: unknown): string => String(id ?? '').trim().toUpperCase();

/** The boot state this bundle reads (ft_boot_inc.php); typed locally — the bundle declares no FateTide global. */
type BootState = { minimapGraph?: GraphPayload | null; user?: { isGM?: boolean }; campaign?: { id?: string } };
const bootState = (): BootState | undefined => (window as unknown as { FateTide?: BootState }).FateTide;

const passageOf = (p: Passage | null | undefined): Passage | null => (p && p.name ? { name: String(p.name), type: String(p.type ?? '') } : null);

export function pairKey(a: string, b: string): string {
    return a < b ? `${a}|${b}` : `${b}|${a}`;
}

function edgeFromWire(a: string, b: string, e: WireEdge): StoreEdge {
    return {
        from: a < b ? a : b,
        to: a < b ? b : a,
        private: e.private === true,
        passage: passageOf(e.passage),
        privatePassage: passageOf(e.private_passage),
    };
}

function notify(campaignId: string): void {
    for (const fn of listeners) {
        try { fn(campaignId); } catch (e) { console.warn('encounter minimap listener failed', e); }
    }
}

export function onMinimapGraphChange(fn: (campaignId: string) => void): void {
    listeners.add(fn);
}

function graphFromPayload(campaignId: string, data: GraphPayload, viewerGm: boolean): CampaignGraph {
    const nodes = new Map<string, StoreNode>();
    for (const n of Array.isArray(data.nodes) ? data.nodes : []) {
        const id = norm(n?.id);
        if (!id) continue;
        const node: StoreNode = { id, name: String(n.name ?? ''), explored: Number(n.explored ?? 0) === 1 };
        if (n.seed && Number.isFinite(n.seed.x) && Number.isFinite(n.seed.y)) {
            node.seed = { x: n.seed.x, y: n.seed.y };
            if (n.seed.frame) node.seed.frame = String(n.seed.frame);
        }
        nodes.set(id, node);
    }
    const edges = new Map<string, StoreEdge>();
    for (const e of Array.isArray(data.edges) ? data.edges : []) {
        const a = norm(e?.from);
        const b = norm(e?.to);
        if (!a || !b || a === b || !nodes.has(a) || !nodes.has(b)) continue;
        edges.set(pairKey(a, b), edgeFromWire(a, b, e));
    }
    return { campaignId, viewerGm, rev: Number(data.rev ?? 0) || 0, reveals: [], roomRevealRev: new Map(), nodes, edges, revision: 1 };
}

const REVEAL_MEMORY = 32;

/** The boot island's graph (this page's campaign), adopted once on first use. */
function seedFromBoot(): void {
    if (bootSeeded) return;
    bootSeeded = true;
    const boot = bootState()?.minimapGraph;
    const campaignId = norm(boot?.campaignId);
    if (boot && campaignId) graphs.set(campaignId, graphFromPayload(campaignId, boot, boot.viewerGm === true));
}

/** This page's graph for a campaign, or null when the page carries none (non-member, another campaign). */
export function getGraph(campaignIdRaw: string): CampaignGraph | null {
    seedFromBoot();
    return graphs.get(norm(campaignIdRaw)) ?? null;
}

/** A frame at or below the held FULL graph's revision is already inside it. */
const isOlder = (graph: CampaignGraph, rev: number | undefined): boolean =>
    typeof rev === 'number' && Number.isFinite(rev) && rev > 0 && rev <= graph.rev;

function mergeReveal(graph: CampaignGraph, reveal: MinimapReveal): boolean {
    const roomId = norm(reveal.encounter_id);
    if (!roomId) return false;
    const room = graph.nodes.get(roomId) ?? { id: roomId, name: '', explored: false };
    room.explored = true;
    if (reveal.name) room.name = String(reveal.name);
    graph.nodes.set(roomId, room);
    for (const n of Array.isArray(reveal.neighbors) ? reveal.neighbors : []) {
        const id = norm(n.id);
        if (!id) continue;
        const known = graph.nodes.get(id);
        if (known) {
            if (Number(n.explored) === 1) known.explored = true;
            if (n.name) known.name = String(n.name);
        } else {
            graph.nodes.set(id, { id, name: String(n.name ?? ''), explored: Number(n.explored) === 1 });
        }
    }
    for (const e of Array.isArray(reveal.edges) ? reveal.edges : []) {
        const a = norm(e.from);
        const b = norm(e.to);
        if (!a || !b || a === b || !graph.nodes.has(a) || !graph.nodes.has(b)) continue;
        graph.edges.set(pairKey(a, b), edgeFromWire(a, b, e));
    }
    return true;
}

/** Merge a room's first-entry reveal in place. */
export function applyReveal(campaignIdRaw: string, reveal: MinimapReveal | null | undefined, rev?: number): void {
    seedFromBoot();
    const campaignId = norm(campaignIdRaw);
    const graph = graphs.get(campaignId);
    if (!graph || !reveal || isOlder(graph, rev)) return;
    const roomId = norm(reveal.encounter_id);
    const hasRev = typeof rev === 'number' && Number.isFinite(rev);
    if (hasRev && (graph.roomRevealRev.get(roomId) ?? -Infinity) >= (rev as number)) return;
    if (!mergeReveal(graph, reveal)) return;
    if (hasRev) {
        graph.roomRevealRev.set(roomId, rev as number);
        graph.reveals.push({ rev: rev as number, reveal });
        if (graph.reveals.length > REVEAL_MEMORY) graph.reveals.shift();
    }
    graph.revision++;
    notify(campaignId);
}

/** Replace the graph with a pushed full rebuild (any authoring edit). */
export function replaceGraph(campaignIdRaw: string, payload: GraphPayload | null | undefined, rev?: number): void {
    seedFromBoot();
    const campaignId = norm(campaignIdRaw);
    if (!campaignId || !payload) return;
    const held = graphs.get(campaignId);
    const frameRev = typeof rev === 'number' ? rev : Number(payload.rev ?? 0);
    if (held && isOlder(held, frameRev)) return;
    // A page that booted without this campaign's graph (e.g. the island failed) adopts the push; the GM view
    // is granted only for the page's OWN campaign, to a viewer its boot state marks as GM.
    const viewerGm = held
        ? held.viewerGm
        : !!bootState()?.user?.isGM && norm(bootState()?.campaign?.id) === campaignId;
    const fresh = graphFromPayload(campaignId, payload, viewerGm);
    fresh.rev = Math.max(fresh.rev, frameRev || 0);
    // Reveals newer than this full graph may have been written after its rebuild read the rooms — keep them,
    // re-applied in revision order (they may have arrived out of order).
    const newer = (held?.reveals ?? []).filter((kept) => kept.rev > fresh.rev).sort((a, b) => a.rev - b.rev);
    for (const kept of newer) {
        if (mergeReveal(fresh, kept.reveal)) {
            fresh.reveals.push(kept);
            fresh.roomRevealRev.set(norm(kept.reveal.encounter_id), kept.rev);
        }
    }
    fresh.revision = (held?.revision ?? 0) + 1;
    graphs.set(campaignId, fresh);
    notify(campaignId);
}

// ── Layout memory ──────────────────────────────────────────────────────────────────────────────────

function memoryKey(graph: CampaignGraph): string {
    return `ft.minimap.layout.v2:${graph.campaignId}`;
}

function readMemory(graph: CampaignGraph): Record<string, LayoutPoint> {
    try {
        const raw = window.localStorage.getItem(memoryKey(graph));
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Record<string, [number, number]>;
        const out: Record<string, LayoutPoint> = {};
        for (const [id, p] of Object.entries(parsed)) {
            if (graph.nodes.has(id) && Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1])) {
                out[id] = { x: p[0], y: p[1] };
            }
        }
        return out;
    } catch {
        return {};
    }
}

function writeMemory(graph: CampaignGraph, positions: Record<string, LayoutPoint>): void {
    try {
        const out: Record<string, [number, number]> = {};
        for (const [id, p] of Object.entries(positions)) {
            if (graph.nodes.has(id)) out[id] = [Math.round(p.x * 1000) / 1000, Math.round(p.y * 1000) / 1000];
        }
        window.localStorage.setItem(memoryKey(graph), JSON.stringify(out));
    } catch { /* private window / quota — memory is a convenience */ }
}

/** Every remembered position for a campaign (localStorage once, then kept in page memory). */
const memoryCache = new Map<string, Record<string, LayoutPoint>>();
function memoryFor(graph: CampaignGraph): Record<string, LayoutPoint> {
    let all = memoryCache.get(graph.campaignId);
    if (!all) {
        all = readMemory(graph);
        memoryCache.set(graph.campaignId, all);
    }
    return all;
}

/**
 * Positions for ONE connected web (the one being drawn), memoized per graph revision. Remembered rooms are
 * pinned in their own frame, so an edited graph never moves them; a web seen for the first time is placed
 * to the right of everything remembered, so webs that later join never overlap.
 */
function positionsFor(graph: CampaignGraph, compIds: string[], compEdges: StoreEdge[]): Record<string, LayoutPoint> {
    const memoKey = `${graph.campaignId}:${compIds[0] ?? ''}:${compIds.length}`;
    const memo = layoutMemo.get(memoKey);
    if (memo && memo.revision === graph.revision) return memo.positions;

    const all = memoryFor(graph);
    const inComp = new Set(compIds);
    const memory: Record<string, LayoutPoint> = {};
    const seeds: Record<string, LayoutSeed> = {};
    for (const id of compIds) {
        const m = all[id];
        if (m) memory[id] = m;
        const seed = graph.nodes.get(id)?.seed;
        if (seed) seeds[id] = seed;
    }
    const positions = layoutMinimap(compIds, compEdges, seeds, memory);
    if (Object.keys(memory).length === 0) {
        let maxX = -Infinity;
        for (const [id, p] of Object.entries(all)) if (!inComp.has(id) && graph.nodes.has(id)) maxX = Math.max(maxX, p.x);
        if (Number.isFinite(maxX)) for (const p of Object.values(positions)) p.x += maxX + 2.0;
    }
    for (const [id, p] of Object.entries(positions)) all[id] = { x: p.x, y: p.y };

    layoutMemo.set(memoKey, { revision: graph.revision, positions });
    writeMemory(graph, all);
    return positions;
}

/**
 * What one mount draws — THE privacy step (owner ruling: the browser filters all). Only the connected web
 * around the mount's room (the campaign graph holds every component). `perspective` 'gm' shows the full web
 * only to a viewer flagged GM; everyone else, and a GM looking through a PC's eyes, gets projectPlayerView:
 * explored + current rooms, nameless frontier stubs, no private link, no secret passage. `reason` is set
 * when there is nothing to chart.
 */
export function buildMinimapView(
    graph: CampaignGraph,
    currentIdRaw: string,
    perspective: 'gm' | 'player',
): { nodes: ViewNode[]; edges: ViewEdge[]; isGmView: boolean; reason: string } {
    const currentId = norm(currentIdRaw);
    const isGmView = graph.viewerGm && perspective === 'gm';
    if (!currentId) return { nodes: [], edges: [], isGmView, reason: 'no_current' };
    if (!graph.nodes.has(currentId)) return { nodes: [], edges: [], isGmView, reason: 'no_links' };

    // The component around the current room, over EVERY link (private ones too — the server's old BFS
    // did the same before filtering).
    const adj = new Map<string, string[]>();
    for (const e of graph.edges.values()) {
        (adj.get(e.from) ?? adj.set(e.from, []).get(e.from))?.push(e.to);
        (adj.get(e.to) ?? adj.set(e.to, []).get(e.to))?.push(e.from);
    }
    const component = new Set<string>([currentId]);
    const queue = [currentId];
    for (let qi = 0; qi < queue.length; qi++) {
        for (const nb of adj.get(queue[qi] as string) ?? []) {
            if (!component.has(nb)) {
                component.add(nb);
                queue.push(nb);
            }
        }
    }
    const rawNodes = [...graph.nodes.values()].filter((n) => component.has(n.id));
    const rawEdges = [...graph.edges.values()].filter((e) => component.has(e.from) && component.has(e.to));

    const positions = positionsFor(graph, [...component].sort(), rawEdges);
    const place = (id: string): LayoutPoint => positions[id] ?? { x: 0, y: 0 };

    let nodes: ViewNode[];
    let edges: ViewEdge[];
    if (isGmView) {
        nodes = rawNodes.map((n) => ({
            id: n.id, name: n.name, explored: n.explored ? 1 : 0, current: n.id === currentId, frontier: false, ...place(n.id),
        }));
        edges = rawEdges.map((e) => ({ from: e.from, to: e.to, private: e.private, passage: e.passage ?? e.privatePassage }));
    } else {
        const projected = projectPlayerView(rawNodes, rawEdges, currentId);
        nodes = projected.nodes.map((n) => ({
            id: n.id, name: n.name, explored: n.explored ? 1 : 0, current: n.id === currentId, frontier: n.frontier, ...place(n.id),
        }));
        edges = projected.edges.map((e) => ({ from: e.from, to: e.to, passage: e.passage }));
    }

    let reason = '';
    if (rawEdges.length === 0) reason = 'no_links';
    else if (edges.length === 0) reason = isGmView ? 'no_links' : 'nothing_explored';
    return { nodes, edges, isGmView, reason };
}

/** Wire the push relay. Idempotent. */
let installed = false;
export function installMinimapStoreListeners(): void {
    if (installed) return;
    installed = true;
    window.addEventListener('ft:minimap-graph-changed', (ev: Event) => {
        const detail = (ev as CustomEvent<{ campaignId?: string; reveal?: MinimapReveal | null; graph?: GraphPayload | null; rev?: number }>).detail;
        const campaignId = String(detail?.campaignId ?? '');
        if (!campaignId) return;
        if (detail?.graph) replaceGraph(campaignId, detail.graph, detail.rev);
        else if (detail?.reveal) applyReveal(campaignId, detail.reveal, detail.rev);
    });
}
